import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import BlogSidebar from "./BlogSidebar";
import { useTranslation } from "react-i18next";
import {
  getArticleByIdByUsers,
  fetchSpeakerByIdByUsers,
} from "../../Api/Users/getUsersApi";
import AOS from "aos";
import "aos/dist/aos.css";

const BlogDetail = () => {
  const { articleId } = useParams();
  const { t, i18n } = useTranslation();
  const languageDirection = i18n.language === "ar" ? "rtl" : "ltr";
  const [article, setArticle] = useState(null);
  const [latestArticles, setLatestArticles] = useState([]);
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const isArabic = i18n.language === "ar";

  useEffect(() => {
    AOS.init();
  }, []);

  useEffect(() => {
    const fetchArticleDetails = async () => {
      try {
        setLoading(true);
        const response = await getArticleByIdByUsers(articleId);
        setArticle(response);

        if (response?.userId) {
          const authorResponse = await fetchSpeakerByIdByUsers(response.userId);
          setAuthor(authorResponse);
        }
      } catch (error) {
        console.error("Error fetching article:", error);
      } finally {
        setLoading(false);
      }
    };

    if (articleId) {
      fetchArticleDetails();
    }
  }, [articleId]);

  if (loading || !article) return <div>Loading...</div>;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language === "ar" ? "ar-EG" : "en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <section className="py-5" dir={languageDirection}>
      <div className="container">
        <div className="row">
          {/* Main Content */}
          <div className="col-lg-8">
            {/* Article Header */}
            <div className="mb-4">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <a href="/" data-i18n="home">
                      {t("home")}
                    </a>
                  </li>
                  <li className="breadcrumb-item">
                    <a href="/blogs" data-i18n="blogs">
                      {t("blogs")}
                    </a>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {article?.title}
                  </li>
                </ol>
              </nav>

              <h1 className="fw-bold text-primary-custom mb-3">
                {isArabic ? article?.title : article?.englishTitle}
              </h1>

              {/* Article Meta Info */}
              <div className="d-flex flex-wrap align-items-center gap-3 mb-4">
                {/* Author Info */}
                {author && (
                  <div className="d-flex align-items-center">
                    <img
                      src={
                        author.profileImage || "https://via.placeholder.com/40"
                      }
                      className="rounded-circle me-2"
                      alt={
                        isArabic
                          ? author?.arabicFullName
                          : author?.englishFullName
                      }
                      width={40}
                      height={40}
                      style={{ objectFit: "cover" }}
                    />
                    <div>
                      <small className="text-muted" data-i18n="author">
                        {t("author")}
                      </small>
                      <p className="mb-0 small fw-medium">
                        {isArabic
                          ? author?.arabicFullName
                          : author?.englishFullName}
                      </p>
                    </div>
                  </div>
                )}

                <div className="vr" />

                <div>
                  <small className="text-muted" data-i18n="posted_on">
                    {t("posted_on")}
                  </small>
                  <p className="mb-0 small fw-medium">
                    {formatDate(article.createdOn)}
                  </p>
                </div>

                <div className="vr" />

                <div>
                  <small className="text-muted">
                    {article.readTime || "7"}{" "}
                    <span data-i18n="minutes_read">{t("minutes_read")}</span>
                  </small>
                </div>
              </div>

              {/* Badges */}
              <div className="mb-4">
                {article.categoryName && (
                  <span className="badge bg-primary-custom me-1">
                    {article.categoryName}
                  </span>
                )}
                {article.articleTypeName && (
                  <span className="badge bg-secondary-custom">
                    {article.articleTypeName}
                  </span>
                )}
              </div>
            </div>

            {/* Feature Image */}
            {article.thumbnailImagePath && (
              <div className="blog-featured-image mb-5">
                <img
                  src={article.thumbnailImagePath}
                  className="img-fluid rounded-4 w-100"
                  alt={article.englishTitle || article.title}
                  style={{ maxHeight: "500px", objectFit: "cover" }}
                />
              </div>
            )}

            {/* Article Content */}
            <div data-aos="fade-up">
              <p>
                {" "}
                {isArabic
                  ? article?.arabicDescription
                  : article?.englishDescription}
              </p>
            </div>

            {/* Tags */}
            {Array.isArray(article?.tags) && article.tags.length > 0 && (
              <div className="mb-5">
                <h3 className="h5 fw-bold mb-3" data-i18n="tags">
                  {t("tags")}
                </h3>
                <div className="d-flex flex-wrap gap-2">
                  {article.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="badge bg-light text-dark text-decoration-none p-2"
                    >
                      {typeof tag === "string" ? tag : tag.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Author Bio */}
            {author && (
              <div className="card bg-light mb-5 border-0 rounded-4">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center">
                    <img
                      src={author?.profileImage || "default-profile.jpg"}
                      className="rounded-circle me-3"
                      alt={
                        isArabic
                          ? author?.arabicFullName
                          : author?.englishFullName
                      }
                      style={{
                        width: "80px",
                        height: "80px",
                        objectFit: "cover",
                      }}
                    />
                    <div>
                      <h4 className="fw-bold mb-1">
                        {isArabic
                          ? author?.arabicFullName
                          : author?.englishFullName}
                      </h4>
                      <p className="text-muted mb-3">
                        {isArabic
                          ? author?.arabicJobTitle
                          : author?.englishJobTitle ||
                            t("expert_in_field")}{" "}
                      </p>
                      <p className="mb-0">
                        {isArabic
                          ? author?.arabicCompleteBiography
                          : author?.englishCompleteBiography}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Share Article */}
            <div className="mb-5">
              <h3 className="h5 fw-bold mb-3" data-i18n="share_article">
                {t("share_article")}
              </h3>

              {(() => {
                const currentUrl = window.location.href;
                const encodedUrl = encodeURIComponent(currentUrl);
                const currentLang = i18n.language;

                const message = encodeURIComponent(
                  currentLang === "ar"
                    ? `📰 اقرأ هذا المقال عبر متحدثون:\n${currentUrl}`
                    : `📰 Read this article on Moutakhidoun:\n${currentUrl}`
                );

                return (
                  <div className="social-share-buttons d-flex flex-wrap gap-2">
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                      className="social-share-btn share-facebook"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-facebook-f"></i> {t("facebook")}
                    </a>
                    <a
                      href={`https://twitter.com/intent/tweet?text=${message}`}
                      className="social-share-btn share-twitter"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i ></i> {t("twitter")}
                    </a>
                    <a
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
                      className="social-share-btn share-linkedin"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-linkedin-in"></i> {t("linkedin")}
                    </a>
                    <a
                      href={`https://api.whatsapp.com/send?text=${message}`}
                      className="social-share-btn share-whatsapp"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-whatsapp"></i> {t("whatsapp")}
                    </a>
                    <a
                      href={`https://t.me/share/url?url=${encodedUrl}&text=${message}`}
                      className="social-share-btn share-telegram"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-telegram-plane"></i> {t("telegram")}
                    </a>
                    <button
                      className="social-share-btn share-copy"
                      onClick={() => {
                        navigator.clipboard.writeText(currentUrl);
                        alert(t("link_copied"));
                      }}
                    >
                      <i className="fas fa-link"></i> {t("copy_link")}
                    </button>
                  </div>
                );
              })()}
            </div>

            {/* Latest Articles */}
            {/* <div className="mb-5">
            <h2 className="fw-bold mb-4">{t("latest_articles")}</h2>
            <div className="row g-4">
              {latestArticles.map((article) => (
                <div key={article.id} className="col-md-6" data-aos="fade-up">
                  <div className="card blog-card h-100 shadow-sm hover-card">
                    <img
                      src={article.imageUrl}
                      className="card-img-top"
                      alt={article.title}
                    />
                    <div className="card-body">
                      <h3 className="card-title h5 fw-bold">{article.title}</h3>
                      <p className="card-text text-muted">
                        {article.description}
                      </p>
                      <div className="d-flex align-items-center mt-3">
                        <img
                          src={article.authorImageUrl}
                          className="rounded-circle me-2"
                          alt={article.author}
                        />
                        <small className="text-muted">{article.author}</small>
                      </div>
                    </div>
                    <div className="card-footer bg-transparent border-0">
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          {article.date?.slice(0, 10)}
                        </small>
                        <a
                          href={`/blog-detail/${article.id}`}
                          className="btn btn-sm btn-link text-primary p-0"
                        >
                          {t("read_more")}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div> */}
          </div>
          <BlogSidebar />
        </div>
      </div>
    </section>
  );
};

export default BlogDetail;
